import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import MediaSlot from "@/components/MediaSlot";
import { getUploads } from "@/components/useSlotUpload";

const resolveSrc = (m) => m?.src || (m?.slot ? getUploads()[m.slot] : null);

const Gallery = ({ items, dark = false, cols = "grid-cols-2 md:grid-cols-3", ratio = "aspect-[4/5]", title = "THE MOMENTS" }) => {
  const [active, setActive] = useState(null);
  const open = (i) => resolveSrc(items[i]) && setActive(i);
  const close = useCallback(() => setActive(null), []);
  const step = useCallback(
    (d) => setActive((a) => (a === null ? a : (a + d + items.length) % items.length)),
    [items.length]
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, close, step]);

  return (
    <div data-testid="media-gallery">
      <p className={`mb-6 font-display text-[10px] font-bold uppercase tracking-[0.35em] ${dark ? "text-paper/50" : "text-ink/50"}`}>
        {title}
      </p>
      <div className={`grid ${cols} gap-3 md:gap-4`}>
        {items.map((m, i) => (
          <MediaSlot
            key={m.slot || i}
            {...m}
            dark={dark}
            ratio={ratio}
            onClick={() => open(i)}
          />
        ))}
      </div>

      <AnimatePresence>
        {active !== null && items[active] && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/95 p-6"
            onClick={close}
          >
            <button data-testid="lightbox-close" onClick={close} className="absolute right-6 top-6 text-paper/70 hover:text-ember" aria-label="Close">
              <X className="h-7 w-7" />
            </button>
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-paper/70 hover:text-ember md:left-10"
              aria-label="Previous"
            >
              <ChevronLeft className="h-10 w-10" />
            </button>
            <motion.figure
              key={active}
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="max-h-[85vh] max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              {items[active].videoSrc ? (
                <video src={items[active].videoSrc} poster={resolveSrc(items[active])} controls autoPlay playsInline className="max-h-[78vh] w-auto max-w-full" />
              ) : (
                <img src={resolveSrc(items[active])} alt={items[active].caption || "gallery"} className="max-h-[78vh] w-auto max-w-full object-contain" />
              )}
              <figcaption className="mt-4 flex items-center justify-between font-display text-xs uppercase tracking-[0.25em] text-paper/60">
                <span>{items[active].caption || "Dokumentasi"}</span>
                <span>{active + 1} / {items.length}</span>
              </figcaption>
            </motion.figure>
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-paper/70 hover:text-ember md:right-10"
              aria-label="Next"
            >
              <ChevronRight className="h-10 w-10" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
